import { useEffect, useState } from 'react';

import { AbstractBankAccount } from '@revenuemarket/bank/src/ui/AGGrid/AGGridBankAccountRenderer';
import ShortedBankAccountInfoCell from '@revenuemarket/bank/src/ui/AGGrid/ShortedBankAccountInfoCell';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { ArrowCircle } from '@revenuemarket/ui';
import { AntColumnType } from '@revenuemarket/ui/antd/Table';
import { convert } from '@revenuemarket/utils';
import { Table, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import { LabeledBankAccountTransaction } from '~apis/cash-flow-overview/transactions/getTransactions';
import { useLabelingTransactions } from '~hooks/queries/transactions/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { CashFlowType } from '~models/cashFlow';

import SettlementItemActionBtn from './SettlementItemActionBtn';
import useSettlementMappingStore, { MappingTransaction } from './SettlementMappingStore';
import SettlementSplittedList from './SettlementSplittedList';

export const headerStyle = {
  background: 'white',
  color: colorTokens.textDescription,
  borderTop: `1px solid ${colorTokens.borderDefault}`,
};

const COLUMNS = ({
  showYear,
  onSelect,
  mappingTransactions,
  originalMappingTransactions,
}: {
  showYear: boolean;
  onSelect: (item: MappingTransaction) => void;

  mappingTransactions: MappingTransaction[];
  originalMappingTransactions: MappingTransaction[];
}): AntColumnType<LabeledBankAccountTransaction> => {
  return [
    {
      title: '거래일자',
      width: 120,
      dataIndex: 'transactionAt',
      key: 'transactionAt',
      render: (value) =>
        value ? (
          showYear ? (
            <Flex direction="column">
              <Typography.Text
                style={{
                  color: colorTokens.textDescription,
                }}
              >
                {dayjs(value).format('YYYY년')}
              </Typography.Text>
              <Typography.Text
                style={{
                  color: colorTokens.textDescription,
                }}
              >
                {dayjs(value).format('M월 D일')}
              </Typography.Text>
            </Flex>
          ) : (
            <Typography.Text
              style={{
                color: colorTokens.textDescription,
              }}
            >
              {dayjs(value).format('M월 D일')}
            </Typography.Text>
          )
        ) : (
          '-'
        ),
    },
    {
      title: '계좌',
      dataIndex: 'account',
      key: 'account',
      width: 140,
      render: (value, record) => {
        return (
          <ShortedBankAccountInfoCell
            data={
              {
                bankCode: record.bankCode,
                accountAlias: record.accountAlias,
                accountNumber: record.accountNumber,
                displayAccountNumber: record.displayAccountNumber,
              } as AbstractBankAccount
            }
          />
        );
      },
    },
    {
      title: '적요 · 거래자명',
      dataIndex: 'description',
      key: 'description',
      width: 190,
      render: (value, record) => {
        return (
          <Flex direction="column" justify="center" style={{ height: '100%' }}>
            <Typography.Text
              style={{
                color: colorTokens.textDefault,
                fontSize: 14,
              }}
            >
              {record.transactionDescription}
            </Typography.Text>
            {convert(record.transactionName, (name) => (
              <Typography.Text
                style={{
                  color: colorTokens.textDescription,
                }}
              >
                {name}
              </Typography.Text>
            ))}
          </Flex>
        );
      },
    },
    {
      title: '구분',
      dataIndex: 'cashFlowType',
      key: 'cashFlowType',
      width: 80,
      render: (value, record) => {
        return (
          <Flex align="center" gap={6}>
            {record?.labelAmount ? (
              <>
                {(record?.labelAmount ?? 0) > 0 ? (
                  <ArrowCircle fill={'#4DC987'} />
                ) : (
                  <ArrowCircle fill={'#FF7A70'} style={{ transform: 'rotate(180deg)' }} />
                )}
              </>
            ) : (
              <div />
            )}
            <Typography.Text
              style={{
                fontSize: 14,
              }}
            >
              {(record?.labelAmount ?? 0) > 0 ? '입금' : '출금'}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      title: '금액',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (value, record) => {
        return (
          <Typography.Text
            style={{
              fontSize: 14,
            }}
          >
            {record?.labelKrwAmount === null
              ? '-'
              : Math.abs(record?.labelKrwAmount ?? 0).toLocaleString() + '원'}
          </Typography.Text>
        );
      },
    },
    {
      title: '',
      key: 'action',
      dataIndex: 'action',
      width: 288,

      render: (_, record) => {
        const getStatus = () => {
          if (
            (record?.mappedObjCount ?? 0) > 0 &&
            !originalMappingTransactions.map((t) => t.transactionId).includes(record.transactionId)
          ) {
            return 'COMPLETED';
          }

          if (mappingTransactions.map((t) => t.transactionId).includes(record.transactionId)) {
            return 'SELECTED';
          }

          return 'NOT_SELECTED';
        };
        return (
          <SettlementItemActionBtn
            mappingTransactions={mappingTransactions}
            originalMappingTransactions={originalMappingTransactions}
            item={record}
            status={getStatus()}
            onSelect={(mappingAmount) => {
              const cashFlowType: CashFlowType = (
                record.transactionInAmount > 0 ? 'IN' : 'OUT'
              ) as CashFlowType;
              onSelect({
                transactionId: record.transactionId,
                cashFlowType,
                transactionAt: record.transactionAt,
                transactionName: record.transactionName,
                transactionDescription: record.transactionDescription,
                totalAmount: Math.abs(record?.labelKrwAmount ?? 0),
                itemCashFlowType: cashFlowType,
                mappingAmount,
                categoryLabelKey: record?.categoryId + '_' + record?.companyDetailedCategoryId,
                groupLabelId: record.customLabelId,
                labelId: record.labelId,
                isFx: record.currencyCode !== 'KRW',
                labelAmount: record.transactionInAmount || record.transactionOutAmount,
              });
            }}
          />
        );
      },
    },
  ];
};

interface Props {
  startDate: Dayjs;
  endDate: Dayjs;
  cashFlowType: CashFlowType;
  searchParam?: string;
}

const SettlementTargetListTable: React.FC<Props> = ({
  startDate,
  endDate,
  cashFlowType,
  searchParam,
}) => {
  const { companyId } = useDemoAwareCompanyId();
  const [type, setType] = useState<CashFlowType>(cashFlowType);
  const { mappingTransactions } = useSettlementMappingStore();
  useEffect(() => {
    setType(cashFlowType);
  }, [cashFlowType]);

  const {
    data: transactionResponse,
    fetchPage,
    page,
    size,
  } = useLabelingTransactions({
    companyId,
    startDate,
    endDate,
    categories: [],
    accounts: [],
    detailedCategoryIds: [],
    categoryLabelKeys: [],
    businessEntities: [],
    customLabelIds: [],
    type,
  });

  useEffect(() => {
    if (searchParam) {
      fetchPage({
        page: 0,
        searchParam,
      });
    } else {
      fetchPage({
        page: 0,
        searchParam: '',
      });
    }
  }, [searchParam]);
  const transactions = transactionResponse?.Data?.data?.content || [];
  const showYear = startDate.year() !== endDate.year();
  const { setMappingTransaction, originalMappingTransactions } = useSettlementMappingStore();

  return (
    <Table
      columns={COLUMNS({
        showYear,
        onSelect: setMappingTransaction,
        mappingTransactions,
        originalMappingTransactions,
      }).map((column) => ({
        ...column,
        ellipsis: true,
        onHeaderCell: () => ({
          ...column.onHeaderCell,
          style: headerStyle,
        }),
      }))}
      scroll={{
        x: '50vw',
        y: '60vh',
      }}
      rowKey={(record) => record.transactionId.toString()}
      dataSource={transactions}
      className={css`
        &&& .ant-table-expanded-row td {
          padding: 0;
        }
      `}
      expandable={{
        showExpandColumn: false,
        expandedRowRender: (record) => {
          return (
            <SettlementSplittedList
              transactionId={record.transactionId}
              accountType={record.accountType}
              originalMappingTransactions={originalMappingTransactions}
            />
          );
        },
        rowExpandable: (record) => record.labelCount > 1,
        expandRowByClick: true,
      }}
      pagination={{
        onChange: (page, pageSize) => {
          fetchPage({ page: page - 1, size: pageSize });
        },
        total: transactionResponse?.Data?.data?.totalElements,
        current: page + 1,
        pageSize: size,
        showSizeChanger: true,
      }}
    />
  );
};

export default SettlementTargetListTable;
